import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import * as XLSX from "xlsx";

import styles from "./InvoicesTable.module.css";
import Loading from "../../components/Loading";
import AddNewButton from "../../components/buttons/AddNewButton";
import CancelButton from "../../components/buttons/CancelButton";
import SaveButton from "../../components/buttons/SaveButton";
import EditButton from "../../components/buttons/EditButton";
import { componentOrderService, invoiceService } from "../../services/api";
import { toDateInputValue, todayIsoDate } from "../../utils/orderDates";
import toShort from "../../utils/dateParser";

const FILTER_CONFIGS = [
  { key: "orderInternalNo", label: "Order #", code: "ON" },
  { key: "customerName", label: "Customer", code: "CU" },
  { key: "invoiceDateMonth", label: "Date", code: "DT" },
  { key: "invoiceStatus", label: "Status", code: "ST" },
  { key: "paymentState", label: "Payment", code: "PY" },
];

const DEFAULT_FILTERS = {
  orderInternalNo: [],
  customerName: [],
  invoiceDateMonth: [],
  invoiceStatus: [],
  paymentState: [],
};

function formatDateTime(value) {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  return d.toLocaleString();
}

function formatMonthBucket(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function formatOrderLabel(o) {
  if (!o) {
    return "";
  }
  return `${o.internalNo?.trim() || `Order #${o.id}`} — ${o.customerName || "—"}`;
}

const getFilterValue = (inv, key) => {
  if (key === "invoiceDateMonth") {
    return formatMonthBucket(inv.invoiceDate);
  }
  if (key === "orderInternalNo") {
    return String(inv.orderInternalNo ?? "").trim();
  }
  if (key === "invoiceStatus") {
    return String(inv.invoiceStatus ?? "").trim();
  }
  if (key === "paymentState") {
    return String(inv.paymentState ?? "").trim();
  }
  return String(inv.customerName ?? "").trim();
};

const matchesFilters = (inv, filters, excludedKey = null) =>
  FILTER_CONFIGS.every(({ key }) => {
    if (key === excludedKey) {
      return true;
    }
    const selectedValues = filters[key] || [];
    if (!selectedValues.length) {
      return true;
    }
    const rowValue = getFilterValue(inv, key);
    return selectedValues.includes(rowValue);
  });

const InvoicesTable = () => {
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterSearch, setFilterSearch] = useState({});
  const filtersFrameRef = useRef(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [draftOrderId, setDraftOrderId] = useState("");
  const [draftInvoiceNumber, setDraftInvoiceNumber] = useState("");
  const [draftInvoiceDate, setDraftInvoiceDate] = useState("");
  const [orderSearchText, setOrderSearchText] = useState("");
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const orderComboRef = useRef(null);
  const [saveError, setSaveError] = useState(null);

  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [invRes, ordRes] = await Promise.all([
        invoiceService.getAll(),
        componentOrderService.getRecent(),
      ]);
      setInvoices(invRes.data.data || []);
      setOrders(ordRes.data.data || []);
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!openFilterKey) {
      return undefined;
    }
    const handleOutsideClick = (event) => {
      const frame = filtersFrameRef.current;
      if (!frame) {
        return;
      }
      const eventPath = typeof event.composedPath === "function" ? event.composedPath() : [];
      const isInside = eventPath.length ? eventPath.includes(frame) : frame.contains(event.target);
      if (!isInside) {
        setOpenFilterKey(null);
      }
    };
    document.addEventListener("pointerdown", handleOutsideClick, true);
    return () => document.removeEventListener("pointerdown", handleOutsideClick, true);
  }, [openFilterKey]);

  useEffect(() => {
    if (!orderDropdownOpen) {
      return undefined;
    }
    const handleOutsideClick = (event) => {
      const el = orderComboRef.current;
      if (!el) {
        return;
      }
      const eventPath = typeof event.composedPath === "function" ? event.composedPath() : [];
      const isInside = eventPath.length ? eventPath.includes(el) : el.contains(event.target);
      if (!isInside) {
        setOrderDropdownOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleOutsideClick, true);
    return () => document.removeEventListener("pointerdown", handleOutsideClick, true);
  }, [orderDropdownOpen]);

  const toggleFilterValue = (filterKey, value) => {
    setFilters((prev) => {
      const existingValues = prev[filterKey];
      const nextValues = existingValues.includes(value)
        ? existingValues.filter((item) => item !== value)
        : [...existingValues, value];
      return { ...prev, [filterKey]: nextValues };
    });
  };

  const clearFilter = (filterKey) => {
    setFilters((prev) => ({ ...prev, [filterKey]: [] }));
    setFilterSearch((prev) => ({ ...prev, [filterKey]: "" }));
  };

  const clearAllFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setFilterSearch({});
  };

  const hasActiveFilters = useMemo(
    () => FILTER_CONFIGS.some(({ key }) => (filters[key] || []).length > 0),
    [filters]
  );

  const filterOptions = useMemo(() => {
    const options = {};
    FILTER_CONFIGS.forEach(({ key }) => {
      const values = new Set();
      invoices.forEach((inv) => {
        const value = getFilterValue(inv, key);
        if (value) {
          values.add(value);
        }
      });
      const optionValues = [...values];
      if (key === "invoiceDateMonth") {
        optionValues.sort((a, b) => b.localeCompare(a));
      } else {
        optionValues.sort((a, b) => a.localeCompare(b));
      }
      options[key] = optionValues;
    });
    return options;
  }, [invoices]);

  const facetCounts = useMemo(() => {
    const countsByFilter = {};
    FILTER_CONFIGS.forEach(({ key }) => {
      const counts = {};
      invoices
        .filter((inv) => matchesFilters(inv, filters, key))
        .forEach((inv) => {
          const value = getFilterValue(inv, key);
          if (!value) {
            return;
          }
          counts[value] = (counts[value] || 0) + 1;
        });
      countsByFilter[key] = counts;
    });
    return countsByFilter;
  }, [invoices, filters]);

  const filteredInvoices = useMemo(
    () => invoices.filter((inv) => matchesFilters(inv, filters)),
    [invoices, filters]
  );

  const pickerFilteredOrders = useMemo(() => {
    const q = orderSearchText.trim().toLowerCase();
    const list = orders;
    if (!q) {
      return list.slice(0, 300);
    }
    return list
      .filter((o) => {
        const no = (o.internalNo || "").toLowerCase();
        const cu = (o.customerName || "").toLowerCase();
        return no.includes(q) || cu.includes(q);
      })
      .slice(0, 300);
  }, [orders, orderSearchText]);

  const buildExportFileName = () => {
    const activeCodes = FILTER_CONFIGS.filter(({ key }) => filters[key].length > 0).map(({ code }) => code);
    const filterSegment = activeCodes.length ? activeCodes.join("-") : "All";
    const now = new Date();
    const stamp = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
      String(now.getSeconds()).padStart(2, "0"),
    ];
    return `Invoices_${filterSegment}_${stamp[0]}-${stamp[1]}-${stamp[2]}-${stamp[3]}-${stamp[4]}-${stamp[5]}.xlsx`;
  };

  const handleRefresh = () => {
    fetchData();
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingInvoiceId(null);
    setSaveError(null);
    setDraftInvoiceNumber("");
    setDraftInvoiceDate("");
    setDraftOrderId("");
    setOrderSearchText("");
    setOrderDropdownOpen(false);
  };

  const handleAddNew = () => {
    setSaveError(null);
    setEditingInvoiceId(null);
    const first = orders[0];
    if (first?.id != null) {
      setDraftOrderId(String(first.id));
      setOrderSearchText(formatOrderLabel(first));
    } else {
      setDraftOrderId("");
      setOrderSearchText("");
    }
    setDraftInvoiceNumber("");
    setDraftInvoiceDate(todayIsoDate());
    setFormOpen(true);
    setOrderDropdownOpen(false);
  };

  const handleEdit = (e, inv) => {
    e.stopPropagation();
    setSaveError(null);
    setEditingInvoiceId(inv.id);
    setDraftOrderId(String(inv.componentOrderId));
    setDraftInvoiceNumber(inv.invoiceNumber || "");
    setDraftInvoiceDate(toDateInputValue(inv.invoiceDate));
    setOrderSearchText(`${inv.orderInternalNo?.trim() || `Order #${inv.componentOrderId}`} — ${inv.customerName || "—"}`);
    setFormOpen(true);
    setOrderDropdownOpen(false);
  };

  const selectOrder = (o) => {
    setDraftOrderId(String(o.id));
    setOrderSearchText(formatOrderLabel(o));
    setOrderDropdownOpen(false);
  };

  const handleSaveInvoice = async () => {
    const orderId = Number.parseInt(draftOrderId, 10);
    if (Number.isNaN(orderId)) {
      setSaveError("Please select an order");
      return;
    }
    const num = draftInvoiceNumber.trim();
    if (!num) {
      setSaveError("Please enter an invoice number");
      return;
    }
    const invDate = draftInvoiceDate.trim();
    if (!invDate) {
      setSaveError("Please select an invoice date");
      return;
    }
    setSaveError(null);
    try {
      if (editingInvoiceId != null) {
        await invoiceService.update(editingInvoiceId, {
          componentOrderId: orderId,
          invoiceNumber: num,
          invoiceDate: invDate,
        });
      } else {
        await invoiceService.create({
          componentOrderId: orderId,
          invoiceNumber: num,
          invoiceDate: invDate,
        });
      }
      closeForm();
      await fetchData();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 409 ? "This invoice number is already in use" : "Failed to save invoice");
      setSaveError(msg);
    }
  };

  const handleExportXlsx = () => {
    const rowsForExport = filteredInvoices.map((inv) => ({
      ID: inv.id,
      "Invoice number": inv.invoiceNumber || "",
      "Invoice date": toShort(inv.invoiceDate),
      Status: inv.invoiceStatus || "",
      "Payment state": inv.paymentState || "",
      "Order #": inv.orderInternalNo || "",
      Customer: inv.customerName || "",
      "Created by": inv.createdBy || "",
      "Created at": formatDateTime(inv.createdAt),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rowsForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
    XLSX.writeFile(workbook, buildExportFileName());
  };

  if (loading) {
    return <Loading loadingText="Loading invoices..." />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerActions}>
          <button
            type="button"
            onClick={handleRefresh}
            className={styles.refreshButton}
            title="Refresh data"
            disabled={loading}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
          {hasPermission("component_orders:create") && (
            <AddNewButton text="+ Add New Invoice" onClick={handleAddNew} disabled={formOpen} />
          )}
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button type="button" onClick={() => setError(null)} className={styles.dismissError}>
            ×
          </button>
        </div>
      )}

      {formOpen && (
        <div className={styles.editForm}>
          <h3 className={styles.editTitle}>{editingInvoiceId != null ? "Edit invoice" : "Add new invoice"}</h3>
          {saveError && <div className={styles.error}>{saveError}</div>}
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <span className={styles.label}>Order</span>
              <div className={styles.orderCombobox} ref={orderComboRef}>
                <div className={styles.orderSearchRow}>
                  <input
                    type="text"
                    className={styles.orderSearchInput}
                    value={orderSearchText}
                    onChange={(e) => {
                      setOrderSearchText(e.target.value);
                      setOrderDropdownOpen(true);
                    }}
                    onFocus={() => setOrderDropdownOpen(true)}
                    placeholder="Search order # or customer…"
                    autoComplete="off"
                    id="invoice-order-search"
                  />
                  {draftOrderId ? (
                    <button
                      type="button"
                      className={styles.orderClearButton}
                      onClick={() => {
                        setDraftOrderId("");
                        setOrderSearchText("");
                      }}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
                {orderDropdownOpen && (
                  <div className={styles.orderDropdown}>
                    {pickerFilteredOrders.length === 0 ? (
                      <div className={styles.orderOptionEmpty}>No matching orders</div>
                    ) : (
                      pickerFilteredOrders.map((o) => (
                        <button
                          key={o.id}
                          type="button"
                          className={styles.orderOption}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => selectOrder(o)}
                        >
                          {formatOrderLabel(o)}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="invoice-number">
                Invoice number
              </label>
              <input
                id="invoice-number"
                className={styles.input}
                value={draftInvoiceNumber}
                onChange={(e) => setDraftInvoiceNumber(e.target.value)}
                placeholder="Enter invoice number"
                autoComplete="off"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="invoice-date">
                Invoice date
              </label>
              <input
                id="invoice-date"
                type="date"
                className={styles.input}
                value={draftInvoiceDate}
                onChange={(e) => setDraftInvoiceDate(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.formActions}>
            <SaveButton
              text={editingInvoiceId != null ? "Save changes" : "Create invoice"}
              onClick={handleSaveInvoice}
            />
            <CancelButton onClick={closeForm} />
          </div>
        </div>
      )}

      <div className={styles.filtersFrame} ref={filtersFrameRef}>
        <p className={styles.filtersLabel}>Filters</p>
        <div className={styles.filtersBar}>
          {FILTER_CONFIGS.map(({ key, label }) => {
            const selectedValues = filters[key] || [];
            const options = filterOptions[key] || [];
            const searchText = (filterSearch[key] || "").toLowerCase();
            const visibleOptions = options.filter((option) => option.toLowerCase().includes(searchText));
            const counts = facetCounts[key] || {};
            const isOpen = openFilterKey === key;
            return (
              <div key={key} className={styles.filterDropdown}>
                <button
                  type="button"
                  className={`${styles.filterTrigger} ${isOpen ? styles.filterTriggerOpen : ""}`}
                  onClick={() => setOpenFilterKey(isOpen ? null : key)}
                >
                  {label}
                  {selectedValues.length > 0 && (
                    <span className={styles.selectedBadge}>{selectedValues.length}</span>
                  )}
                </button>
                {isOpen && (
                  <div className={styles.dropdownPanel}>
                    <input
                      type="text"
                      className={styles.filterSearchInput}
                      placeholder="Search options…"
                      value={filterSearch[key] || ""}
                      onChange={(event) =>
                        setFilterSearch((prev) => ({
                          ...prev,
                          [key]: event.target.value,
                        }))
                      }
                    />
                    <div className={styles.dropdownOptions}>
                      {visibleOptions.length === 0 ? (
                        <div className={styles.emptyOption}>No options</div>
                      ) : (
                        visibleOptions.map((option) => (
                          <label key={option} className={styles.optionItem}>
                            <input
                              type="checkbox"
                              checked={selectedValues.includes(option)}
                              onChange={() => toggleFilterValue(key, option)}
                            />
                            <span>{option}</span>
                            <span className={styles.optionCount}>[{counts[option] || 0}]</span>
                          </label>
                        ))
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.clearFilterButton}
                      onClick={() => clearFilter(key)}
                      disabled={!selectedValues.length}
                    >
                      Clear filter
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <div className={styles.filtersActions}>
            <button
              type="button"
              className={styles.secondaryActionButton}
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
            >
              Clear all filters
            </button>
            {hasPermission("component_orders:export") && (
              <button
                type="button"
                className={styles.exportButton}
                onClick={handleExportXlsx}
                disabled={filteredInvoices.length === 0}
              >
                Export .xlsx
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th>Invoice #</th>
              <th>Invoice date</th>
              <th>Status</th>
              <th>Payment</th>
              <th>Order #</th>
              <th>Customer</th>
              <th>Created by</th>
              <th>Created at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.noData}>
                  No invoices yet.
                  {hasPermission("component_orders:create") ? " Click “Add New Invoice” to create one." : ""}
                </td>
              </tr>
            ) : filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.noData}>
                  No invoices match the selected filters.
                </td>
              </tr>
            ) : (
              filteredInvoices.map((inv) => (
                <tr
                  key={inv.id}
                  className={`${styles.tableRow} ${styles.clickableRow}`}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <td className={styles.invoiceNumber}>{inv.invoiceNumber}</td>
                  <td className={styles.date}>{toShort(inv.invoiceDate)}</td>
                  <td>{inv.invoiceStatus || "—"}</td>
                  <td>{inv.paymentState || "—"}</td>
                  <td className={styles.orderNumber}>{inv.orderInternalNo || "—"}</td>
                  <td>{inv.customerName || "—"}</td>
                  <td>{inv.createdBy || "—"}</td>
                  <td className={styles.date}>{formatDateTime(inv.createdAt)}</td>
                  <td className={styles.actions} onClick={(e) => e.stopPropagation()}>
                    {hasPermission("component_orders:update") && (
                      <EditButton onClick={(e) => handleEdit(e, inv)} disabled={formOpen} />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoicesTable;
